import {AstView, EnrichedAstNode} from '../../types';
import {isNodeInsideBox} from '../../helpers/GeometryHelpers';
import {checkGaps} from '../GapMethods';
import {setupAbsolutePositionParent, absolutePositionSingleChildren} from '../CssAdjustments';

/** builds a map of what overlaps what */
function findNodeOverlaps(nodes: EnrichedAstNode[]): {[nodeId: string]: {overlaps: string[]; node: EnrichedAstNode}} {
    const overlapMap: {[nodeId: string]: {overlaps: string[]; node: EnrichedAstNode}} = {};

    nodes.forEach((childA) => {
        overlapMap[childA.internalId] = {overlaps: [], node: childA};
        nodes.forEach((childB) => {
            if (childA.internalId === childB.internalId) {
                return;
            }
            // if overlap push to map.
            const overlap = isNodeInsideBox(childB, childA.position);
            if (overlap.inside || overlap.overlap) {
                overlapMap[childA.internalId].overlaps.push(childB.internalId);
            }
        });
    });

    return overlapMap;
}

function findIdToRemove(children: EnrichedAstNode[]) {
    const map = findNodeOverlaps(children);
    const filtered = Object.values(map).filter((entry) => entry.overlaps.length > 0);

    const preferAbsElements = filtered.filter(
        (entry) => entry.node.inputLayoutAbstraction.absolutePosition === undefined
    );
    const nonAbsElements = filtered.filter((entry) => entry.node.inputLayoutAbstraction.absolutePosition !== undefined);

    const list = preferAbsElements.length > 0 ? preferAbsElements : nonAbsElements;

    const sortedEntry = list.sort((a, b) => {
        if (a.overlaps.length !== b.overlaps.length) {
            return a.overlaps.length - b.overlaps.length;
        }
        return a.node.zIndex - b.node.zIndex;
    });
    if (sortedEntry.length === 0) {
        return null;
    }

    return sortedEntry[0];
}

/** Remove any overlapping nodes that prevent us from doing flex positioning. */
export function makeReadyForFlex(view: AstView, node: EnrichedAstNode): void {
    const children = node.children;
    const position = node.innerPosition;

    if (node.type === 'Text') {
        return;
    }

    node.children.forEach((child) => {
        if (child.styles.transform) {
            absolutePositionSingleChildren(view, node, child);
            setupAbsolutePositionParent(node);
        }
        if (child.calculatedLayoutAbstraction?.absolutePosition) {
            absolutePositionSingleChildren(view, node, child);
            setupAbsolutePositionParent(node);
        }
    });

    const flexChildren = children.filter((node) => node.styles.position !== 'absolute');
    // check gaps
    const gaps = checkGaps(position, flexChildren);
    // if can slice then return.

    if (flexChildren.length < 2) {
        return;
    }

    if (gaps.x.length >= 3 || gaps.y.length >= 3) {
        return;
    }

    // find node to remove. (if we have nodes left)
    const nodeToAdjustEntry = findIdToRemove(children);
    if (nodeToAdjustEntry === null) {
        return;
    }

    // remove node.
    setupAbsolutePositionParent(node);
    absolutePositionSingleChildren(view, node, nodeToAdjustEntry.node);

    // recurse.
    return makeReadyForFlex(view, node);
}

export function findNodesThatNeedAbsolutePositioning(
    node: EnrichedAstNode,
    currentAbsolutePositionedList: string[] = [],
    respectCalculatedNodes = false
) {
    const absolutePositionedChildList = [...currentAbsolutePositionedList];

    if (node.type === 'Text') {
        return absolutePositionedChildList;
    }

    node.children.forEach((child) => {
        if (absolutePositionedChildList.includes(child.internalId) || child.userInput.userSelectedParentId) {
            return;
        }
        if (child.styles.transform) {
            absolutePositionedChildList.push(child.internalId);
        }
        if (child.inputLayoutAbstraction.absolutePosition) {
            absolutePositionedChildList.push(child.internalId);
        }
        if (child.systemLayoutAbstraction.absolutePosition) {
            absolutePositionedChildList.push(child.internalId);
        }
        if (respectCalculatedNodes && child.calculatedLayoutAbstraction?.absolutePosition) {
            absolutePositionedChildList.push(child.internalId);
        }
    });

    const possibleFlexChildren = node.children.filter(
        (child) => !absolutePositionedChildList.includes(child.internalId)
    );
    const position = node.innerPosition;

    // check gaps
    const gaps = checkGaps(position, possibleFlexChildren);
    // if can slice then return.

    if (possibleFlexChildren.length < 2) {
        return absolutePositionedChildList;
    }

    if (gaps.x.length >= 3 || gaps.y.length >= 3) {
        return absolutePositionedChildList;
    }

    // find node to remove. (if we have nodes left)
    const nodeToAdjustEntry = findIdToRemove(possibleFlexChildren);
    if (nodeToAdjustEntry === null) {
        absolutePositionedChildList.push(...possibleFlexChildren.map((node) => node.internalId));
        return absolutePositionedChildList;
    }

    absolutePositionedChildList.push(nodeToAdjustEntry.node.internalId);

    // recurse.
    return findNodesThatNeedAbsolutePositioning(node, absolutePositionedChildList);
}
